import { CSSProperties, FC, RefObject, useMemo, useCallback, useState, useEffect, useRef, } from "react";
import ReactDOM from "react-dom";
// 方法
import classNames from "classnames";
// 常量
import { AutoMatchDropdownBaseProps, AutoMatchOption, RuleProps } from "../../interface";

interface DropdownProps extends AutoMatchDropdownBaseProps {
  /** 指定当前选中的条目 */
  value?: string;
  /** 是否显示 */
  show?: boolean;
  /** 距离页面左侧的距离 */
  left?: number;
  /** 距离页面右侧的距离 */
  top?: number;
  /** 当前选中的匹配规则 */
  selectedRule?: RuleProps,
  /** dropdownRef */
  dropdownRef: RefObject<HTMLDivElement>;
  /** 最小宽度 */
  width: number,
  /** 点击选项 */
  onClickItem: (optionValue: string, optionItem: RuleProps) => void;
}

const Dropdown: FC<DropdownProps> = props => {
  const {
    value: inputValue, rules: outRules, valueEmprtTip, ruleEmprtTip,
    popupClassName, dropdownStyle, show, left, top, listHeight, dropdownRef, width, loading, selectedRule,
    getPopupContainer, onClickItem,
  } = props;
  const [options, setOptions] = useState<AutoMatchOption[]>(); //选项
  const [activeValue, setActiveValue] = useState<string>(); //当前选项状态为active的选项值
  const autoSelected = useRef<boolean>(true); // 是否自动设置第一个选项为active状态
  const timer = useRef<any>(); // 键盘事件定时器
  useEffect(() => {
    const newOptions: AutoMatchOption[] = [];
    if (inputValue && outRules) {
      outRules.forEach(item => {
        const {label, value, prefix} = item;
        if (!label || !value) return;
        if (prefix) {
          if (inputValue.startsWith(prefix)) {
            newOptions.push({label, value});
          }
        } else {
          newOptions.push({label, value});
        }
      });
    }
    if (newOptions.length && !selectedRule && autoSelected.current) {
      setActiveValue(newOptions[0].value);
    }
    setOptions(newOptions);
  }, [inputValue, outRules]);
  useEffect(() => {
    if (show) {
      window.addEventListener('keydown', handleKeydown);
    } else {
      autoSelected.current = true;
    }
    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [show, options, activeValue, selectedRule]);
  /** 鼠标移入选项 */
  const handleItemMouseEnter = (optionValue: string) => {
    autoSelected.current = false;
    setActiveValue(optionValue);
  };
  /** 键盘事件 */
  const handleKeydown = useCallback((event: any) => {
    timer.current && clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (!options) return;
      const keyNum = event.key;
      if (keyNum === 'ArrowDown' || keyNum === 'ArrowUp') {
        if (options.length < 2) return;
        autoSelected.current = false;
        const findIndex = activeValue ? options.findIndex(item => item.value === activeValue) : options.findIndex(item => item.value === selectedRule?.value);
        let nextIndex = keyNum === 'ArrowDown' ? findIndex + 1 : findIndex - 1;
        if (nextIndex >= options.length) nextIndex = 0;
        if (nextIndex < 0) nextIndex = options.length - 1;
        setActiveValue(options[nextIndex]?.value);
        return;
      }
      if (keyNum === 'Enter' && (activeValue || selectedRule)) {
        const findItem = activeValue ? outRules?.find(item => item.value === activeValue) : outRules?.find(item => item.value === selectedRule?.value);
        setActiveValue(undefined);
        findItem && onClickItem((activeValue || selectedRule?.value) as string, findItem);
      }
    }, 100);
  }, [options, activeValue, selectedRule]);
  /** 点击选项 */
  const handleClickItem = (optionValue: string, optionItem: RuleProps) => {
    setActiveValue(undefined);
    onClickItem(optionValue, optionItem);
  }
  const innerDropdownStyle: CSSProperties = useMemo(() => {
    const newDropdownStyle: CSSProperties = {
      width,
      transformOrigin: 'center top',
      ...dropdownStyle,
    };
    if (!show) {
      newDropdownStyle.display = 'none';
    } else {
      newDropdownStyle.left = left;
      newDropdownStyle.top = (top || 0) + 4;
    }
    return newDropdownStyle;
  }, [show, left, top]);
  const dropdownClasses = useMemo(() => {
    return classNames('yb-associate-select-dropdown', popupClassName);
  }, [popupClassName]);
  const renderHTML = (
    <div ref={dropdownRef} className={dropdownClasses} style={innerDropdownStyle} >
      <div className="associate-select-dropdown-list" style={{maxHeight: listHeight || 300}}>
        {
          loading ? (
            <div className="yb-associate-select-dropdown-empty">
              数据加载中
            </div>
          ) : (
            <>
              {
                !!options?.length ? options.map(item => {
                  const {label, value} = item;
                  const isSelect = selectedRule && selectedRule.value === item.value;
                  const isActive = value === activeValue;
                  const itemClasses = classNames('dropdown-list-item', {
                    'option-select': isSelect,
                    'option-active': isActive,
                  });
                  return (
                    <div
                      key={value}
                      className={itemClasses}
                      title={label}
                      onClick={() => handleClickItem(value as string, item)}
                      onMouseEnter={() => handleItemMouseEnter(value as string)}
                    >
                      <div className="dropdown-list-item-option yb-line-ellipsis-1">
                        <span>{label}</span>
                        {
                          !!inputValue && <span className="p-r-s">:</span>
                        }
                        <span>{inputValue}</span>
                      </div>
                    </div>
                  );
                }) : (
                  <div className="yb-associate-select-dropdown-empty">
                    {inputValue ? ruleEmprtTip : valueEmprtTip}
                  </div>
                )
              }
            </>
          )
        }
      </div>
    </div>
  );
  const containerDom: HTMLElement = useMemo(() => {
    if (!getPopupContainer) return document.body;
    if (typeof getPopupContainer === 'function') return getPopupContainer();
    return getPopupContainer;
  }, [getPopupContainer]);
  return ReactDOM.createPortal(renderHTML, containerDom);
};

export default Dropdown;
